import { useEffect, useState, FC, ReactNode } from "react";

import "./dev.scss";
import {
  RubiPermissionModel,
  listPermission,
  putPermission,
  deletePermission,
} from "@rubi/api-generated";
import { Link } from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";

const DoActionQuiz: FC<{
  promptText: string;
  actionText: string;
  onCorrect: (answer: string) => void;
  checkAnswer: (answer: string) => boolean;
}> = ({ promptText, actionText, onCorrect, checkAnswer }) => {
  const [text, setText] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);
  return (
    <>
      <span>{promptText}</span>
      <input value={text} onChange={(e) => setText(e.target.value)} />
      <button
        onClick={() => {
          if (checkAnswer(text)) {
            setDisabled(true);
            onCorrect(text);
          }
        }}
        disabled={disabled}
      >
        {actionText}
      </button>
    </>
  );
};

const ButtonSpoiler: FC<{
  spoilerText?: string;
  children?: ReactNode;
}> = ({ spoilerText = "show", children }) => {
  const [hidden, setHidden] = useState<boolean>(true);
  if (hidden) {
    return <button onClick={() => setHidden(false)}>{spoilerText}</button>;
  } else {
    return <>{children}</>;
  }
};

const Permission: FC<RubiPermissionModel> = ({ id, title }) => {
  const [renamePermissionErrorMsg, setRenamePermissionErrorMsg] = useState<
    string | null
  >(null);
  const [deletePermissionErrorMsg, setDeletePermissionErrorMsg] = useState<
    string | null
  >(null);

  const doRenamePermission = async (title: string) => {
    const response = await putPermission({
      path: { permission_id: id },
      body: { title },
      throwOnError: false,
    });
    if (response.status !== 200) {
      setRenamePermissionErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
    }
  };

  const doDeletePermission = async () => {
    const response = await deletePermission({
      path: { permission_id: id },
      throwOnError: false,
    });
    if (response.status !== 200) {
      setDeletePermissionErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
    }
  };

  return (
    <tr>
      <td>
        <Link to={"./" + id} state={{ permission: { id, title } }}>
          {id}
        </Link>
      </td>
      <td>{title}</td>
      <td>
        <ButtonSpoiler spoilerText="Rename">
          <DoActionQuiz
            promptText="New name: "
            actionText="Rename"
            onCorrect={(answerTitle) => doRenamePermission(answerTitle)}
            checkAnswer={(answerTitle) => /^([a-z0-9_]+\.)+$/.test(answerTitle)}
          />
          <ErrorMsg msg={renamePermissionErrorMsg} />
        </ButtonSpoiler>
      </td>
      <td>
        <ButtonSpoiler spoilerText="Delete">
          <DoActionQuiz
            promptText="Enter 'byebye ' followed by the permission title: "
            actionText="Delete"
            onCorrect={(_answerTitle) => doDeletePermission()}
            checkAnswer={(answerTitle) => answerTitle === "byebye " + title}
          />
          <ErrorMsg msg={deletePermissionErrorMsg} />
        </ButtonSpoiler>
      </td>
    </tr>
  );
};

const PermissionTable: FC<{ permissions: RubiPermissionModel[] }> = ({
  permissions,
}) => {
  return (
    <table className="dev-table">
      <thead>
        <tr>
          <th>id</th>
          <th>title</th>
          <th>Rename</th>
          <th>Delete</th>
        </tr>
      </thead>
      <tbody>
        {permissions.map((permission) => (
          <Permission key={permission.id} {...permission} />
        ))}
      </tbody>
    </table>
  );
};

export const DevPermissionsPage: FC = () => {
  const [permissionsList, setPermissionsList] = useState<RubiPermissionModel[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [createPermissionPermissionId, setCreatePermissionPermissionId] =
    useState<string>("1");
  const [createPermissionTitle, setCreatePermissionTitle] = useState<string>(
    "resource.scope.action.",
  );
  const [createPermissionErrorMsg, setCreatePermissionErrorMsg] = useState<
    string | null
  >(null);

  const loadPermissions = async () => {
    const response = await listPermission({ throwOnError: false });
    if (response.status === 200) {
      const data = response.data!;
      setPermissionsList(data);
      setError(null);
    } else {
      setPermissionsList([]);
      setError("" + response.status + " " + response.error?.console_msg);
    }
  };

  const doCreatePermission = async (permission_id: number, title: string) => {
    const response = await putPermission({
      path: { permission_id },
      body: { title },
      throwOnError: false,
    });
    if (response.status !== 200) {
      setCreatePermissionErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
    }
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  return (
    <DevPage
      title="Permissions"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>Permissions</Crumb>
        </Breadcrumbs>
      }
    >
      <Section subtitle="Create permission">
        <span>Permission id: </span>
        <input
          value={createPermissionPermissionId}
          onChange={(e) => setCreatePermissionPermissionId(e.target.value)}
        />
        <span>Title: </span>
        <input
          value={createPermissionTitle}
          onChange={(e) => setCreatePermissionTitle(e.target.value)}
        />
        <button
          onClick={() => {
            const permissionId = parseInt(createPermissionPermissionId);
            if (isNaN(permissionId)) {
              setCreatePermissionErrorMsg("[no request made] invalid id");
            } else {
              doCreatePermission(permissionId, createPermissionTitle);
            }
          }}
        >
          Create
        </button>
        <ErrorMsg msg={createPermissionErrorMsg} />
      </Section>
      <Section>
        <button onClick={loadPermissions}>Reload</button>
        <ErrorMsg msg={error} />
        <PermissionTable permissions={permissionsList} />
      </Section>
    </DevPage>
  );
};
